import React, { useCallback, useEffect, useState } from 'react'
import GetNewUserReportTable from './Table/GetNewUserReport'
import APIErrorComponent from '../../../../../../components/_shared/Error/APIErrorComponent'
import { useLocation } from 'react-router-dom'
import { fetchIncidentAnalysisData } from '../../../../../../../utils/api/reportAPI'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import jsPDF from 'jspdf'
import 'jspdf-autotable'
import axios from 'axios'
import { useSelector } from 'react-redux'
import { useSearchParams } from 'react-router-dom'

const GetNewUserReport = () => {
    const location = useLocation()
    const { token } = useSelector((state) => state?.auth)
    const [currentRow, setCurrentRow] = useState(null)
    const queryClient = useQueryClient()
    const [sort, setSort] = useState(null)
    const [tableOptions, setTableOptions] = useState({
        rowPerPages: 10,
        activePage: 0,
    })

    const [searchParams] = useSearchParams()
    const [payload, setPayload] = useState(null)

    useEffect(() => {

        // Read from URL params
        const month = searchParams.get('month')
        const year = searchParams.get('year')
        const code = searchParams.get('code')

        if (month || year) {
            const urlPayload = {
                month: Number(month),
                year: Number(year),
                code
            }

            setPayload(urlPayload)

            sessionStorage.setItem(
                'newUserReportPayload',
                JSON.stringify(urlPayload)
            )

            return
        }

        // fallback to location.state
        if (location.state) {
            setPayload(location.state)

            sessionStorage.setItem(
                'newUserReportPayload',
                JSON.stringify(location.state)
            )

            return
        }

        // fallback to sessionStorage
        const storedPayload = sessionStorage.getItem('newUserReportPayload')

        if (storedPayload) {
            setPayload(JSON.parse(storedPayload))
        }

    }, [location.state, searchParams])

    const handlePageClick = (event) => {
        setTableOptions((prev) => ({ ...prev, activePage: event.selected }))
    }

    const handleRowsPerPage = (e) => {
        setTableOptions((prev) => ({
            ...prev,
            rowPerPages: e.target.value,
            activePage: 0,
        }))
    }

    const getNewUserReport = useCallback(async () => {
        let apiQueryParams = {
            per_page: tableOptions.rowPerPages,
            page: tableOptions.activePage + 1,
            export: 'N',
        }
        if (sort && sort.columnName && sort.order) {
            apiQueryParams = {
                ...apiQueryParams,
                order: sort.order,
                sort: sort.columnName,
            }
        }

        return fetchIncidentAnalysisData({ ...payload, ...apiQueryParams })
    }, [sort, payload, tableOptions])

    const {
        isLoading,
        isFetching,
        data: NewUserReport,
        isError,
        error,
    } = useQuery(
        ['fetch-new-user-report', payload, sort, tableOptions],
        () => getNewUserReport(),
        {
             enabled: !!payload,
            keepPreviousData: false,
  },
        {
            retry: (failureCount, error) => {
                const status = error?.response?.status
                return failureCount < 1 && status >= 400 && status < 600
            },
            retryDelay: 1000,
        }
    )

    const pageCount = Math.ceil(
        (NewUserReport?.total || 0) / tableOptions.rowPerPages
    )

    const handleColumnSort = async (column, sortDirection) => {
        setSort({
            columnName: column.sortField,
            order: sortDirection,
        })
    }

    const downloadPDF = async () => {
        try {
            const payload = {
                ...location.state,
                export: 'N',
                pagination: 'false',
            }

            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                payload,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            )

            const fullData = response?.data?.data || []
            if (!Array.isArray(fullData) || fullData.length === 0) return

            const doc = new jsPDF({
                orientation: 'portrait',
                unit: 'mm',
                format: 'a4',
            })

            // Title
            doc.setFontSize(18)
            doc.text('New User Report', 14, 18)

            // Congregation
            doc.setFontSize(12)
            doc.text(
                `Congregation: ${fullData[0]?.user_congregation || 'N/A'}`,
                14,
                26
            )

            const tableColumn = [
                'Name',
                'Company',
                'Address',
                'Congregation',
                'Device Count',
            ]

            const tableRows = fullData.map((item) => [
                item?.name_tx || '',
                item?.company_tx || '',
                item?.address_tx || '',
                item?.user_congregation || '',
                item?.device_count || 0,
            ])

            doc.autoTable({
                startY: 34,
                head: [tableColumn],
                body: tableRows,

                // Clean enterprise-style theme
                theme: 'grid',

                headStyles: {
                    fillColor: [60, 90, 190], // Softer blue
                    textColor: [255, 255, 255],
                    fontSize: 11,
                    fontStyle: 'bold',
                    halign: 'center',
                    cellPadding: 4,
                },

                bodyStyles: {
                    fontSize: 10,
                    cellPadding: 3,
                    textColor: [50, 50, 50],
                },

                alternateRowStyles: {
                    fillColor: [245, 245, 245], // Subtle grey
                },

                styles: {
                    lineColor: [180, 180, 180], // Soft border
                    lineWidth: 0.2,
                    overflow: 'linebreak',
                    valign: 'middle',
                },

                columnStyles: {
                    0: { cellWidth: 35 },
                    1: { cellWidth: 35 },
                    2: { cellWidth: 60 },
                    3: { cellWidth: 35 },
                    4: { cellWidth: 25, halign: 'center' },
                },
            })

            // Totals
            const totalUsers = fullData.length
            const totalDevices = fullData.reduce(
                (sum, item) => sum + (item.device_count || 0),
                0
            )

            const finalY = doc.lastAutoTable.finalY + 10

            doc.setFontSize(12)
            doc.text(`Total New Users: ${totalUsers}`, 14, finalY)
            doc.text(`Total Devices: ${totalDevices}`, 14, finalY + 6)

            doc.save('NewUserReport.pdf')
        } catch (error) {
            console.error('PDF Export Error:', error)
        }
    }

    const downloadFile = (data, fileName = 'download') => {
        const csvBlob = new Blob([data], { type: 'text/csv' })
        const csvUrl = URL.createObjectURL(csvBlob)
        const hiddenElement = document.createElement('a')
        hiddenElement.href = csvUrl
        hiddenElement.target = '_blank'
        hiddenElement.download = `${fileName}.csv`
        hiddenElement.click()
    }

    const downloadExcel = async () => {
        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_BACKEND_SERVICE_URL}/reports/report_data`,
                {
                    ...location.state,
                    export: 'Y',
                    pagination: 'false',
                },
                {
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`,
                    },
                }
            )

            downloadFile(response?.data, 'new-user-report')
        } catch (error) {
            console.error('Excel Export Error:', error)
        }
    }

    return (
        <>
            {isError ? (
                <APIErrorComponent error={error} />
            ) : (
                <>
                    <GetNewUserReportTable
                        currentRow={currentRow}
                        isLoading={isLoading || isFetching}
                        data={NewUserReport?.data}
                        handleColumnSort={handleColumnSort}
                        downloadPDF={downloadPDF}
                        downloadExcel={downloadExcel}
                        setCurrentRow={setCurrentRow}
                        tableOptions={tableOptions}
                        handlePageClick={handlePageClick}
                        handleRowsPerPage={handleRowsPerPage}
                        pageCount={pageCount}
                        totaldata={NewUserReport?.total}
                         reportCode={payload?.code}
                    />
                </>
            )}
        </>
    )
}

export default GetNewUserReport
